/**
 * WxbGanttChart v2 — Constants
 * Aligned with wxb-theme.css design tokens
 */
import type { GanttTheme } from './types';

// ===== Theme Colors =====
const readThemeToken = (token: string, fallback: string): string => {
  if (typeof window === 'undefined' || !window.document) return fallback;
  const value = window.getComputedStyle(document.documentElement).getPropertyValue(token).trim();
  return value || fallback;
};

export const THEME: GanttTheme = {
  primary:      readThemeToken('--wx-blue-700', '#0B3D7F'),
  primaryHover: readThemeToken('--wx-blue-800', '#0A3470'),
  success:      readThemeToken('--wx-green-500', '#2E9D6E'),
  warning:      readThemeToken('--wx-amber-500', '#E8B53C'),
  danger:       readThemeToken('--wx-red-500', '#D6493A'),
  ink:          readThemeToken('--wx-ink', '#0F1B2D'),
  fg2:          readThemeToken('--wx-fg-2', '#3A4A5C'),
  fg3:          readThemeToken('--wx-fg-3', '#5A6B7E'),
  fg4:          readThemeToken('--wx-fg-4', '#8898A8'),
  border:       readThemeToken('--wx-border', '#E4EAF1'),
  divider:      readThemeToken('--wx-divider', '#EEF2F7'),
  surface1:     readThemeToken('--wx-surface-1', '#FAFCFE'),
  surface2:     readThemeToken('--wx-surface-2', '#F5F8FB'),
  surface3:     readThemeToken('--wx-surface-3', '#EDF1F6'),
  bg:           readThemeToken('--wx-bg', '#FFFFFF'),
  blue500:      readThemeToken('--wx-blue-500', '#1F6FEB'),
  blue400:      readThemeToken('--wx-blue-400', '#5A93F0'),
  blue300:      readThemeToken('--wx-blue-300', '#9DBEF5'),
  blue100:      readThemeToken('--wx-blue-100', '#E6F2FB'),
  green500:     readThemeToken('--wx-green-500', '#2E9D6E'),
  green300:     readThemeToken('--wx-green-300', '#A3D9BF'),
  amber500:     readThemeToken('--wx-amber-500', '#E8B53C'),
  red500:       readThemeToken('--wx-red-500', '#D6493A'),
};

// ===== Stage Colors (5-color rotation) =====
export const STAGE_COLORS = [
  THEME.primary,
  THEME.success,
  readThemeToken('--wx-cyan-500', '#2A9DB0'),
  THEME.warning,
  THEME.danger,
  THEME.blue500,
  THEME.fg3,
];

// ===== Dimensions =====
export const ROW_HEIGHT = 32;
export const HEADER_HEIGHT = 48;       // Time axis header
export const HEATMAP_HEIGHT = 16;      // Personnel heatmap bar
export const BAR_HEIGHT = 24;          // Task bar height
export const STAGE_BAR_HEIGHT = 20;    // Stage bar (narrower)
export const BAR_RADIUS = 6;           // Task bar corner radius
export const STAGE_BAR_RADIUS = 4;
export const SIDEBAR_WIDTH = 200;
export const TOOLBAR_HEIGHT = 40;
export const ARROW_SIZE = 9;           // Dependency arrow triangle
export const LABEL_CAPSULE_HEIGHT = 18;
export const LABEL_CAPSULE_RADIUS = 9;

// ===== Zoom =====
export const DEFAULT_DAY_WIDTH = 120;
export const MIN_DAY_WIDTH = 40;
export const MAX_DAY_WIDTH = 600;
export const ZOOM_SENSITIVITY = 0.002;

// ===== Drag =====
export const SNAP_HOURS = 0.25;        // 15-minute snap
export const DRAG_THRESHOLD = 5;       // px before drag starts
export const MAX_UNDO = 10;

// ===== Constraint line styles =====
export const DEP_STYLES: Record<string, { color: string; dash: number[] }> = {
  FS: { color: THEME.blue500, dash: [] },
  SS: { color: THEME.success, dash: [6, 4] },
  FF: { color: THEME.warning, dash: [4, 4] },
  SF: { color: readThemeToken('--wx-cyan-500', '#2A9DB0'), dash: [12, 4] },
};

// ===== Share link colors =====
export const SHARE_COLORS = {
  SAME_TEAM:  THEME.blue500,
  DIFFERENT:  THEME.warning,
};

// ===== Fonts =====
export const FONT_SANS = '"Inter", "PingFang SC", "Source Han Sans SC", system-ui, sans-serif';
export const FONT_MONO = '"JetBrains Mono", "SF Mono", monospace';
